import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Lang = "es" | "en" | "pt";

const STORAGE_KEY = "dm_lang";

/** Detecta el idioma según país/idioma del navegador (Brasil → pt, EEUU → en, resto → es). */
export function detectLang(): Lang {
  if (typeof window === "undefined") return "es";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as Lang | null;
    if (stored === "es" || stored === "en" || stored === "pt") return stored;
  } catch {
    /* ignore */
  }

  const tz = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {
      return "";
    }
  })();

  // País por zona horaria (buen proxy de geolocalización sin pedir permisos)
  if (/^America\/(Sao_Paulo|Bahia|Fortaleza|Recife|Manaus|Belem|Cuiaba|Campo_Grande|Porto_Velho|Boa_Vista|Rio_Branco|Maceio|Araguaina|Santarem|Noronha|Eirunepe)/.test(tz)) {
    return "pt";
  }
  const usTz =
    /^America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Detroit|Indiana|Kentucky|Boise|Juneau|Sitka|Nome|Menominee|North_Dakota)/.test(tz) ||
    tz === "Pacific/Honolulu" ||
    tz === "US/Eastern" ||
    tz === "US/Central" ||
    tz === "US/Mountain" ||
    tz === "US/Pacific";
  if (usTz) return "en";

  const langs = [navigator.language, ...(navigator.languages ?? [])].filter(Boolean).map((l) => l.toLowerCase());
  if (langs.some((l) => l.startsWith("pt"))) return "pt";
  if (langs.some((l) => l.startsWith("en"))) return "en";
  return "es";
}

type Dict = Record<string, string>;

const es: Dict = {
  "nav.venta": "Venta",
  "nav.alquiler": "Alquiler",
  "nav.proyectos": "Proyectos",
  "nav.blog": "Blog",
  "nav.agente": "Agente",
  "nav.sell": "Vendé tu propiedad",
  "brand.tagline": "Bienes Raíces",
  "menu.open": "Abrir menú",
  "lang.label": "Idioma",

  "hero.badge": "Asunción · Central · Paraguay",
  "hero.title1": "Tu próximo hogar,",
  "hero.title2": "a un clic.",
  "hero.subtitle":
    "Cartera curada de propiedades premium en Villa Morra, Recoleta, Las Lomas y todo Paraguay. Asesoría personalizada en cada paso.",
  "search.buy": "Comprar",
  "search.rent": "Alquilar",
  "search.temp": "Temporal",
  "search.operation": "Operación",
  "search.venta": "Venta",
  "search.alquiler": "Alquiler",
  "search.alquiler_temporal": "Alquiler temporal",
  "search.type": "Tipo de propiedad",
  "type.casas": "Casas",
  "type.departamentos": "Departamentos",
  "type.terrenos": "Terrenos",
  "type.oficinas": "Oficinas",
  "type.locales": "Locales",
  "type.duplex": "Dúplex",
  "search.placeholder": "Barrio o zona (ej: villa-morra)",
  "search.submit": "Buscar",
  "search.popular": "Populares:",
  "popular.1": "Casas en Villa Morra",
  "popular.2": "Deptos en Recoleta",
  "popular.3": "Alquiler Las Lomas",

  "home.featured": "Propiedades destacadas",
  "home.featured.sub": "Selección personal de Dahiana.",
  "home.blog": "Últimas del blog",
  "home.blog.sub": "Tips y noticias del mercado inmobiliario.",
  "home.blog.cta": "Ver todos",
  "home.zones": "Zonas destacadas",
  "home.zones.sub": "Explorá los barrios más buscados de Asunción.",
  "home.zones.cta": "Ver propiedades",
  "home.sell.title": "¿Querés vender tu propiedad?",
  "home.sell.sub": "Tasación gratuita, fotos profesionales y una base de compradores activos.",
  "home.sell.cta": "Solicitar tasación",

  "card.featured": "Destacada",
  "card.opportunity": "Oportunidad",
  "card.nophoto": "Sin foto",

  "filters.title": "Filtros",
  "filters.currency": "Moneda",
  "filters.all": "Todas",
  "filters.minPrice": "Precio min.",
  "filters.maxPrice": "Precio max.",
  "filters.beds": "Dormitorios (mín.)",
  "filters.any": "Cualquiera",
  "filters.minArea": "Superficie mín. (m²)",
  "filters.clear": "Limpiar filtros",
  "filters.alerts": "Recibir alertas por email",
  "list.loading": "Cargando…",
  "list.count": "propiedades",
  "list.empty": "No encontramos propiedades con estos filtros. Probá con otros criterios.",
  "sort.label": "Ordenar por",
  "sort.recent": "Más recientes",
  "sort.price_asc": "Precio: menor a mayor",
  "sort.price_desc": "Precio: mayor a menor",

  "footer.about":
    "Asesoría inmobiliaria en Asunción y Central. Acompañamiento personalizado en compra, venta y alquiler.",
  "footer.properties": "Propiedades",
  "footer.company": "Empresa",
  "footer.contact": "Contacto",
  "footer.casasVenta": "Casas en venta",
  "footer.deptosVenta": "Departamentos en venta",
  "footer.deptosAlquiler": "Departamentos en alquiler",
  "footer.terrenos": "Terrenos",
  "footer.about.link": "Sobre Dahiana",
  "footer.rights": "Todos los derechos reservados.",
  "footer.city": "Asunción, Paraguay",
};

const en: Dict = {
  "nav.venta": "For sale",
  "nav.alquiler": "For rent",
  "nav.proyectos": "Projects",
  "nav.blog": "Blog",
  "nav.agente": "Agent",
  "nav.sell": "Sell your property",
  "brand.tagline": "Real Estate",
  "menu.open": "Open menu",
  "lang.label": "Language",

  "hero.badge": "Asunción · Central · Paraguay",
  "hero.title1": "Your next home,",
  "hero.title2": "one click away.",
  "hero.subtitle":
    "A curated portfolio of premium properties in Villa Morra, Recoleta, Las Lomas and all of Paraguay. Personal guidance every step of the way.",
  "search.buy": "Buy",
  "search.rent": "Rent",
  "search.temp": "Short stay",
  "search.operation": "Operation",
  "search.venta": "For sale",
  "search.alquiler": "For rent",
  "search.alquiler_temporal": "Short-term rental",
  "search.type": "Property type",
  "type.casas": "Houses",
  "type.departamentos": "Apartments",
  "type.terrenos": "Land",
  "type.oficinas": "Offices",
  "type.locales": "Retail spaces",
  "type.duplex": "Duplexes",
  "search.placeholder": "Neighborhood or area (e.g. villa-morra)",
  "search.submit": "Search",
  "search.popular": "Popular:",
  "popular.1": "Houses in Villa Morra",
  "popular.2": "Apartments in Recoleta",
  "popular.3": "Rentals in Las Lomas",

  "home.featured": "Featured properties",
  "home.featured.sub": "Dahiana's personal selection.",
  "home.blog": "Latest from the blog",
  "home.blog.sub": "Tips and news from the real estate market.",
  "home.blog.cta": "See all",
  "home.zones": "Top neighborhoods",
  "home.zones.sub": "Explore the most sought-after areas of Asunción.",
  "home.zones.cta": "View properties",
  "home.sell.title": "Want to sell your property?",
  "home.sell.sub": "Free valuation, professional photos and a base of active buyers.",
  "home.sell.cta": "Request a valuation",

  "card.featured": "Featured",
  "card.opportunity": "Deal",
  "card.nophoto": "No photo",

  "filters.title": "Filters",
  "filters.currency": "Currency",
  "filters.all": "All",
  "filters.minPrice": "Min. price",
  "filters.maxPrice": "Max. price",
  "filters.beds": "Bedrooms (min.)",
  "filters.any": "Any",
  "filters.minArea": "Min. area (m²)",
  "filters.clear": "Clear filters",
  "filters.alerts": "Get email alerts",
  "list.loading": "Loading…",
  "list.count": "properties",
  "list.empty": "No properties match these filters. Try different criteria.",
  "sort.label": "Sort by",
  "sort.recent": "Most recent",
  "sort.price_asc": "Price: low to high",
  "sort.price_desc": "Price: high to low",

  "footer.about":
    "Real estate advisory in Asunción and Central. Personal support for buying, selling and renting.",
  "footer.properties": "Properties",
  "footer.company": "Company",
  "footer.contact": "Contact",
  "footer.casasVenta": "Houses for sale",
  "footer.deptosVenta": "Apartments for sale",
  "footer.deptosAlquiler": "Apartments for rent",
  "footer.terrenos": "Land",
  "footer.about.link": "About Dahiana",
  "footer.rights": "All rights reserved.",
  "footer.city": "Asunción, Paraguay",
};

const pt: Dict = {
  "nav.venta": "Venda",
  "nav.alquiler": "Aluguel",
  "nav.proyectos": "Projetos",
  "nav.blog": "Blog",
  "nav.agente": "Corretora",
  "nav.sell": "Venda seu imóvel",
  "brand.tagline": "Imóveis",
  "menu.open": "Abrir menu",
  "lang.label": "Idioma",

  "hero.badge": "Assunção · Central · Paraguai",
  "hero.title1": "Seu próximo lar,",
  "hero.title2": "a um clique.",
  "hero.subtitle":
    "Portfólio selecionado de imóveis premium em Villa Morra, Recoleta, Las Lomas e todo o Paraguai. Assessoria personalizada em cada etapa.",
  "search.buy": "Comprar",
  "search.rent": "Alugar",
  "search.temp": "Temporada",
  "search.operation": "Operação",
  "search.venta": "Venda",
  "search.alquiler": "Aluguel",
  "search.alquiler_temporal": "Aluguel por temporada",
  "search.type": "Tipo de imóvel",
  "type.casas": "Casas",
  "type.departamentos": "Apartamentos",
  "type.terrenos": "Terrenos",
  "type.oficinas": "Escritórios",
  "type.locales": "Lojas",
  "type.duplex": "Duplex",
  "search.placeholder": "Bairro ou região (ex: villa-morra)",
  "search.submit": "Buscar",
  "search.popular": "Populares:",
  "popular.1": "Casas em Villa Morra",
  "popular.2": "Apartamentos na Recoleta",
  "popular.3": "Aluguel em Las Lomas",

  "home.featured": "Imóveis em destaque",
  "home.featured.sub": "Seleção pessoal da Dahiana.",
  "home.blog": "Últimas do blog",
  "home.blog.sub": "Dicas e notícias do mercado imobiliário.",
  "home.blog.cta": "Ver todos",
  "home.zones": "Bairros em destaque",
  "home.zones.sub": "Explore os bairros mais procurados de Assunção.",
  "home.zones.cta": "Ver imóveis",
  "home.sell.title": "Quer vender seu imóvel?",
  "home.sell.sub": "Avaliação gratuita, fotos profissionais e uma base de compradores ativos.",
  "home.sell.cta": "Solicitar avaliação",

  "card.featured": "Destaque",
  "card.opportunity": "Oportunidade",
  "card.nophoto": "Sem foto",

  "filters.title": "Filtros",
  "filters.currency": "Moeda",
  "filters.all": "Todas",
  "filters.minPrice": "Preço mín.",
  "filters.maxPrice": "Preço máx.",
  "filters.beds": "Quartos (mín.)",
  "filters.any": "Qualquer",
  "filters.minArea": "Área mín. (m²)",
  "filters.clear": "Limpar filtros",
  "filters.alerts": "Receber alertas por e-mail",
  "list.loading": "Carregando…",
  "list.count": "imóveis",
  "list.empty": "Não encontramos imóveis com esses filtros. Tente outros critérios.",
  "sort.label": "Ordenar por",
  "sort.recent": "Mais recentes",
  "sort.price_asc": "Preço: menor para maior",
  "sort.price_desc": "Preço: maior para menor",

  "footer.about":
    "Assessoria imobiliária em Assunção e Central. Acompanhamento personalizado na compra, venda e aluguel.",
  "footer.properties": "Imóveis",
  "footer.company": "Empresa",
  "footer.contact": "Contato",
  "footer.casasVenta": "Casas à venda",
  "footer.deptosVenta": "Apartamentos à venda",
  "footer.deptosAlquiler": "Apartamentos para alugar",
  "footer.terrenos": "Terrenos",
  "footer.about.link": "Sobre a Dahiana",
  "footer.rights": "Todos os direitos reservados.",
  "footer.city": "Assunção, Paraguai",
};

const DICTS: Record<Lang, Dict> = { es, en, pt };

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: (key: string) => string };

const LanguageContext = createContext<Ctx>({ lang: "es", setLang: () => {}, t: (k) => es[k] ?? k });

export function LanguageProvider({
  children,
  urlLang,
  onLangChange,
}: {
  children: ReactNode;
  /** Idioma tomado del query param `?lang=` (visible para el SSR y los buscadores). */
  urlLang?: Lang;
  /** Sincroniza el idioma elegido con la URL. */
  onLangChange?: (l: Lang) => void;
}) {
  const [lang, setLangState] = useState<Lang>(urlLang ?? "es");

  useEffect(() => {
    if (urlLang) {
      setLangState(urlLang);
      try {
        window.localStorage.setItem(STORAGE_KEY, urlLang);
      } catch {
        /* ignore */
      }
      return;
    }
    // Sin parámetro en la URL: detectamos y, si no es español, lo reflejamos en la URL
    // para que el contenido SEO servido también quede en ese idioma.
    const detected = detectLang();
    setLangState(detected);
    if (detected !== "es") onLangChange?.(detected);
  }, [urlLang, onLangChange]);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<Ctx>(
    () => ({
      lang,
      setLang: (l: Lang) => {
        setLangState(l);
        try {
          window.localStorage.setItem(STORAGE_KEY, l);
        } catch {
          /* ignore */
        }
        onLangChange?.(l);
      },
      t: (key: string) => DICTS[lang][key] ?? es[key] ?? key,
    }),
    [lang, onLangChange],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}


export function useI18n() {
  return useContext(LanguageContext);
}
